import { useRef, useState } from "react";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ClienteDaApi } from "./cliente";
import { recursoInexistente } from "./problema";
import { salvar } from "./salvar";
import type {
  AlteracaoDeCobranca,
  AlteracaoDeConta,
  AlteracaoDeConvenio,
  AlteracaoDePessoa,
  AlteracaoDePlano,
  AmostraDaFilaMorta,
  CobrancaDoSistema,
  ConferenciaDoCertificado,
  CorrecaoDeNomeDoSistema,
  ContaDaPessoa,
  ConvenioNovo,
  ConveniosDoVinculo,
  LeituraDoCertificado,
  LinhaDaAuditoria,
  LinkDaFicha,
  ListaDaAuditoria,
  ListaDeBancos,
  ListaDeBoletos,
  ListaDeContas,
  ListaDeEntregas,
  PedidoDeProvisionamento,
  PessoaDetalhe,
  LimiteDoVinculo,
  ListaDeCedentes,
  ListaDeVinculosDoBeneficiario,
  SituacaoDoVinculo,
  Vinculo,
  VinculoNovo,
  CadastroCriado,
  Contrato,
  ContratoNovo,
  DetalheDoPlano,
  EncerramentoDeContrato,
  ListaDeContratos,
  ListaDePlanos,
  PlanoAlterado,
  PlanoNovo,
  TrocaDePlano,
  TrocaRealizada,
  ContaNova,
  EntregaConfigurada,
  EntregaDoPainel,
  PedidoDeSituacaoDoSistema,
  PessoaNova,
  RedeNova,
  RedePermitida,
  ReenvioAgendado,
  ReenvioDeEntrega,
  SistemaNovo,
  SituacaoPedidaDoSistema,
  BoletoDoPainel,
  EventoDeBoleto,
  IndicadorDeFilas,
  ListaDeDivergencias,
  ListaDeDocumentos,
  ListaDePessoas,
  ListaDeSistemas,
  ListaDeConvenios,
  DetalheDoSistema,
  DetalheDoConvenio,
  TecnicoDoBoleto,
  TituloOrfao,
  VisaoGeral,
  BaixaDeFatura,
  CancelamentoDeFatura,
  Fatura,
  ListaDeFaturas,
  PedidoDeFechamento,
  PreviaDaCompetencia,
  ResultadoDoFechamento,
  SimulacaoDeFechamento,
} from "./contrato";

/*
 * Polling, e não WebSocket nem SSE (doc 06 §3). São dois ou três operadores;
 * tempo real não paga o próprio custo aqui.
 *
 * Os intervalos vêm do doc 06 §3, por tipo de tela.
 */
export const INTERVALOS = {
  /** Visão geral: é a tela que denuncia incidente em andamento. */
  operacional: 10_000,
  /** Listas: boletos, fila morta, divergências. */
  listas: 30_000,
  /** Detalhe de um boleto. */
  detalhe: 15_000,
} as const;

export const CHAVES = {
  visaoGeral: ["visao-geral"] as const,
  boletos: (filtros: Record<string, string | undefined>) => ["boletos", filtros] as const,
  boleto: (id: string) => ["boleto", id] as const,
  eventos: (id: string) => ["boleto", id, "eventos"] as const,
  tecnico: (id: string) => ["boleto", id, "tecnico"] as const,
  divergencias: ["divergencias"] as const,
  /* Debaixo de `titulosOrfaos`: invalidar o prefixo leva as duas listas da aba. */
  titulosOrfaos: ["titulos-orfaos"] as const,
  listaDeOrfaos: (incluirTratados: boolean) => ["titulos-orfaos", incluirTratados] as const,
  filas: ["filas"] as const,
  amostra: (fila: string) => ["filas", fila, "amostra"] as const,
  documentos: ["documentos"] as const,
  entregas: (filtros: Record<string, string | undefined>) => ["entregas", filtros] as const,
  auditoria: (filtros: Record<string, string | undefined>) => ["auditoria", filtros] as const,
  saudeProvider: ["saude-provider"] as const,
  sistemas: ["sistemas"] as const,
  sistema: (id: string) => ["sistema", id] as const,
  /*
   * A página fica SOB o prefixo da lista antiga: invalidar `pessoas` leva as duas,
   * e nenhuma escrita precisa saber quais filtros estão montados.
   */
  pessoas: ["pessoas"] as const,
  paginaDePessoas: (filtros: Record<string, string | number | undefined>) =>
    ["pessoas", "pagina", filtros] as const,
  /*
   * Contas e vínculos ficam sob a chave do cedente, pelo mesmo motivo do contrato
   * sob o sistema: invalidar o cedente leva tudo o que depende da prontidão dele.
   */
  pessoa: (id: string) => ["pessoa", id] as const,
  contas: (pessoaID: string) => ["pessoa", pessoaID, "contas"] as const,
  conta: (pessoaID: string, contaID: string) => ["pessoa", pessoaID, "contas", contaID] as const,
  convenios: ["convenios"] as const,
  paginaDeConvenios: (filtros: Record<string, string | number | undefined>) =>
    ["convenios", "pagina", filtros] as const,
  convenio: (id: string) => ["convenio", id] as const,
  bancos: ["bancos"] as const,
  planos: ["planos"] as const,
  plano: (id: string) => ["plano", id] as const,
  /*
   * Debaixo da chave do sistema de propósito: invalidar o detalhe da aplicação
   * (prefixo) leva o histórico de contratos junto, e uma ação de contrato que
   * esquecesse um dos dois deixaria o cabeçalho e a aba discordando.
   */
  contratos: (sistemaID: string, pessoaID?: string) =>
    pessoaID === undefined
      ? (["sistema", sistemaID, "contratos"] as const)
      : (["sistema", sistemaID, "contratos", pessoaID] as const),
  /** Também sob o sistema: o contrato do par muda a linha do cedente. */
  cedentes: (sistemaID: string) => ["sistema", sistemaID, "cedentes"] as const,
  vinculosDaPessoa: (pessoaID: string) => ["pessoa", pessoaID, "vinculos"] as const,
  /*
   * Sob o mesmo prefixo `faturas`: a baixa, o cancelamento e o fechamento
   * invalidam o prefixo, e a lista, a prévia e o detalhe voltam juntos — uma
   * fatura recém-gerada some da prévia (o evento foi ligado) e aparece na lista
   * no mesmo instante.
   */
  faturas: (filtros: Record<string, string | undefined>) => ["faturas", "lista", filtros] as const,
  previa: (filtros: Record<string, string | undefined>) => ["faturas", "previa", filtros] as const,
  fatura: (id: string) => ["faturas", "detalhe", id] as const,
};

/*
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ PAUSA QUANDO A ABA NÃO ESTÁ VISÍVEL (doc 06 §3).                        │
 * │                                                                          │
 * │ Três operadores com aba aberta o dia inteiro, a 10 s, são 1.080          │
 * │ requisições por hora contra um VPS de 6 vCPU — e a maior parte delas     │
 * │ para uma tela que ninguém está olhando.                                  │
 * │                                                                          │
 * │ `refetchIntervalInBackground: false` é o padrão do TanStack Query, e     │
 * │ está escrito porque é decisão, não descuido.                             │
 * └──────────────────────────────────────────────────────────────────────────┘
 */
const POLLING = {
  refetchIntervalInBackground: false,
  refetchOnWindowFocus: true,
  /*
   * Sem retentativa automática: as respostas de erro do painel são acionáveis
   * — `401` é sessão, `403` é papel, `503` é fonte fora — e repetir três vezes
   * antes de mostrar a mensagem certa só atrasa quem diagnostica um incidente.
   */
  retry: false,
} as const;

export function useVisaoGeral(cliente: ClienteDaApi, habilitada: boolean) {
  return useQuery({
    queryKey: CHAVES.visaoGeral,
    queryFn: ({ signal }) =>
      cliente.obter<VisaoGeral>("/admin/v1/visao-geral", { sinal: signal }),
    enabled: habilitada,
    refetchInterval: INTERVALOS.operacional,
    ...POLLING,
  });
}

export type FiltrosDeBoletos = {
  q?: string | undefined;
  situacao?: string | undefined;
  banco?: string | undefined;
  sistema_id?: string | undefined;
  /** O cedente do boleto. */
  pessoa_id?: string | undefined;
  convenio_id?: string | undefined;
  vence_de?: string | undefined;
  vence_ate?: string | undefined;
  criado_de?: string | undefined;
  criado_ate?: string | undefined;
  valor_minimo?: string | undefined;
  valor_maximo?: string | undefined;
  /** `"true"` traz o conjunto, e `"false"`, o complemento. Outro valor é `400`. */
  indeterminado?: string | undefined;
  /** Parado há mais de 45 minutos, sem ninguém responsável. `"true"` ou `"false"`. */
  presos?: string | undefined;
  /** O conjunto que a visão geral conta em `envio_incerto`. `"true"` ou `"false"`. */
  envio_em_aberto?: string | undefined;
  /** Um estado do envio. Fora do conjunto é `400`. */
  envio_estado?: string | undefined;
  antes?: string | undefined;
};

export function useBoletos(
  cliente: ClienteDaApi,
  filtros: FiltrosDeBoletos,
  habilitada: boolean,
) {
  return useQuery({
    queryKey: CHAVES.boletos(filtros),
    queryFn: ({ signal }) =>
      cliente.obter<ListaDeBoletos>("/admin/v1/boletos", { busca: filtros, sinal: signal }),
    enabled: habilitada,
    refetchInterval: INTERVALOS.listas,
    /*
     * Mantém a página anterior enquanto a nova carrega. Sem isso, cada tecla
     * digitada na busca esvazia a tabela e a redesenha — e o operador lê uma
     * tela piscando em vez de uma lista se refinando.
     */
    placeholderData: (anterior) => anterior,
    ...POLLING,
  });
}

/*
 * Link colado com identificador que não existe não passa a existir daqui a quinze
 * segundos: o polling para, e só com essa resposta. `503` é fonte fora, e aí insistir é o
 * que devolve a tela quando a fonte voltar.
 */
function intervaloDoDetalhe(consulta: { state: { error: unknown } }) {
  return recursoInexistente(consulta.state.error) ? false : INTERVALOS.detalhe;
}

export function useBoleto(cliente: ClienteDaApi, id: string | null) {
  return useQuery({
    queryKey: CHAVES.boleto(id ?? ""),
    queryFn: ({ signal }) =>
      cliente.obter<BoletoDoPainel>(`/admin/v1/boletos/${id ?? ""}`, { sinal: signal }),
    enabled: id !== null,
    refetchInterval: intervaloDoDetalhe,
    ...POLLING,
  });
}

export function useEventos(cliente: ClienteDaApi, id: string | null) {
  return useQuery({
    queryKey: CHAVES.eventos(id ?? ""),
    queryFn: ({ signal }) =>
      cliente.obter<{ eventos: EventoDeBoleto[] }>(`/admin/v1/boletos/${id ?? ""}/eventos`, {
        sinal: signal,
      }),
    enabled: id !== null,
    refetchInterval: intervaloDoDetalhe,
    ...POLLING,
  });
}

/**
 * A aba técnica.
 *
 * Só é buscada quando a aba está ABERTA — e isso não é otimização de rede: a
 * rota devolve PII sem máscara e **grava uma linha de auditoria a cada
 * chamada**. Buscá-la junto com o detalhe encheria `auditoria_painel` de
 * acessos a dado pessoal que ninguém pediu, e tornaria a trilha inútil
 * justamente para a pergunta que ela existe para responder: quem olhou o quê.
 */
export function useTecnico(cliente: ClienteDaApi, id: string | null, abaAberta: boolean) {
  return useQuery({
    queryKey: CHAVES.tecnico(id ?? ""),
    queryFn: ({ signal }) =>
      cliente.obter<TecnicoDoBoleto>(`/admin/v1/boletos/${id ?? ""}/tecnico`, {
        sinal: signal,
      }),
    enabled: id !== null && abaAberta,
    // Sem polling: cada ciclo seria mais uma linha de auditoria.
    refetchInterval: false,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

/*
 * As ações de escrita.
 *
 * Toda uma invalida o que mudou **imediatamente**, sem esperar o próximo ciclo
 * de polling (doc 06 §3). O operador que cancela um boleto precisa ver a lista
 * refletir isso — trinta segundos de tela mentindo é tempo suficiente para ele
 * clicar de novo achando que não funcionou.
 */
export function useAcaoSobreBoleto(cliente: ClienteDaApi, id: string) {
  const cache = useQueryClient();

  return useMutation({
    mutationFn: async (acao: {
      caminho: string;
      metodo?: "POST" | "PUT" | "DELETE";
      corpo?: unknown;
    }) =>
      cliente.enviar<{ aviso?: string; operacao_prevista?: string; situacao?: string }>(
        `/admin/v1/boletos/${id}/${acao.caminho}`,
        {
          metodo: acao.metodo ?? "POST",
          ...(acao.corpo !== undefined ? { corpo: acao.corpo } : {}),
        },
      ),
    onSuccess: () => {
      /*
       * A visão geral entra na invalidação porque a contagem de indeterminados
       * muda com a resolução — e ela vive na barra lateral, visível em toda
       * tela. Deixá-la desatualizada faria o contador contradizer a ação que
       * o operador acabou de tomar.
       */
      void cache.invalidateQueries({ queryKey: ["boletos"] });
      void cache.invalidateQueries({ queryKey: CHAVES.boleto(id) });
      void cache.invalidateQueries({ queryKey: CHAVES.eventos(id) });
      void cache.invalidateQueries({ queryKey: CHAVES.visaoGeral });
    },
  });
}

/** O que a confirmação manda: a justificativa e os parecidos que o operador leu na tela. */
export interface PedidoDeReprocesso {
  justificativa: string;
  parecidosConferidos: readonly string[];
}

/**
 * Reprocessar um boleto em `FALHA`: o gesto mais perigoso do painel.
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ A JUSTIFICATIVA VAI NO CORPO, E O GATE É DA API.                         │
 * │                                                                          │
 * │ `202` diz que o boleto voltou à fila, e não que o título saiu. A leitura │
 * │ vem também na recusa: um `409` quer dizer que a tela estava velha, e o   │
 * │ detalhe precisa mostrar por quê. `exact` porque a aba técnica mora sob   │
 * │ esta chave, e reler a aba grava uma linha de auditoria.                  │
 * │                                                                          │
 * │ O detalhe é a única leitura que o `onSettled` devolve: quem espera o     │
 * │ `mutateAsync` só volta a confirmar com a lista nova na tela. Sem isso, o │
 * │ botão reabre com a lista velha, e o clique leva a mesma recusa.          │
 * └──────────────────────────────────────────────────────────────────────────┘
 */
export function useReprocessarBoleto(cliente: ClienteDaApi, id: string) {
  const cache = useQueryClient();

  return useMutation({
    mutationFn: ({ justificativa, parecidosConferidos }: PedidoDeReprocesso) =>
      cliente.enviar<{ situacao: string; aviso: string }>(
        `/admin/v1/boletos/${id}/reprocessamento`,
        { metodo: "POST", corpo: { justificativa, parecidos_conferidos: parecidosConferidos } },
      ),
    onSettled: () => {
      void cache.invalidateQueries({ queryKey: ["boletos"] });
      void cache.invalidateQueries({ queryKey: CHAVES.eventos(id) });
      void cache.invalidateQueries({ queryKey: CHAVES.visaoGeral });
      return cache.invalidateQueries({ queryKey: CHAVES.boleto(id), exact: true });
    },
  });
}

/**
 * A sonda: pergunta ao provider se o título existe.
 *
 * `202`, sem resultado: quem pergunta é o conciliador, e a resposta aparece no detalhe. Relê
 * também na recusa, como "Verificar agora": `sonda_recente` diz que outro operador acabou de
 * pedir, e o instante dele está no detalhe.
 */
export function useSondarBoleto(cliente: ClienteDaApi, id: string) {
  const cache = useQueryClient();

  return useMutation({
    mutationFn: () =>
      cliente.enviar<{ sonda_pedida_em: string }>(`/admin/v1/boletos/${id}/sonda`, {
        metodo: "POST",
      }),
    onSettled: () => cache.invalidateQueries({ queryKey: CHAVES.boleto(id), exact: true }),
  });
}

/* ── Operação ────────────────────────────────────────────────────────────── */

/**
 * Os títulos do provider sem correspondência nos nossos boletos.
 *
 * Sem paginação na API: a rota corta no teto, e a rodada é noturna, o que dá o intervalo das
 * listas de sobra.
 */
export function useTitulosOrfaos(
  cliente: ClienteDaApi,
  incluirTratados: boolean,
  habilitada: boolean,
) {
  return useQuery({
    queryKey: CHAVES.listaDeOrfaos(incluirTratados),
    queryFn: ({ signal }) =>
      cliente.obter<{ titulos_orfaos: TituloOrfao[] }>("/admin/v1/titulos-orfaos", {
        busca: incluirTratados ? { incluir_tratados: "true" } : {},
        sinal: signal,
      }),
    enabled: habilitada,
    refetchInterval: INTERVALOS.listas,
    ...POLLING,
  });
}

/**
 * Marcar um órfão como tratado: registra quem olhou, e nada além disso. Relê também na recusa:
 * `orfao_ja_tratado` diz que a lista estava velha.
 */
export function useTratarTituloOrfao(cliente: ClienteDaApi) {
  const cache = useQueryClient();

  return useMutation({
    mutationFn: (pedido: { id: string; justificativa: string }) =>
      cliente.enviar<void>(`/admin/v1/titulos-orfaos/${pedido.id}/tratamento`, {
        metodo: "POST",
        corpo: { justificativa: pedido.justificativa },
      }),
    onSettled: () => {
      void cache.invalidateQueries({ queryKey: CHAVES.titulosOrfaos });
      // A contagem vive na visão geral, que está na moldura em toda tela.
      void cache.invalidateQueries({ queryKey: CHAVES.visaoGeral });
    },
  });
}

export function useDivergencias(cliente: ClienteDaApi, habilitada: boolean) {
  return useQuery({
    queryKey: CHAVES.divergencias,
    queryFn: ({ signal }) =>
      cliente.obter<ListaDeDivergencias>("/admin/v1/divergencias", { sinal: signal }),
    enabled: habilitada,
    refetchInterval: INTERVALOS.listas,
    ...POLLING,
  });
}

export function useFilas(cliente: ClienteDaApi, habilitada: boolean) {
  return useQuery({
    queryKey: CHAVES.filas,
    queryFn: ({ signal }) =>
      cliente.obter<IndicadorDeFilas>("/admin/v1/filas", { sinal: signal }),
    enabled: habilitada,
    refetchInterval: INTERVALOS.listas,
    ...POLLING,
  });
}

/**
 * A espiada na fila morta.
 *
 * Só busca quando uma fila é escolhida — a rota abre canal no broker e lê até
 * cinquenta mensagens, devolvendo cada uma. É barato, mas não é de graça, e
 * fazê-lo em polling contínuo seria conversar com o broker o dia inteiro para
 * uma tela que ninguém está olhando.
 */
export function useAmostra(cliente: ClienteDaApi, fila: string | null) {
  return useQuery({
    queryKey: CHAVES.amostra(fila ?? ""),
    queryFn: ({ signal }) =>
      cliente.obter<AmostraDaFilaMorta>(
        `/admin/v1/filas/${encodeURIComponent(fila ?? "")}/amostra`,
        { sinal: signal },
      ),
    enabled: fila !== null,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

export function useDocumentos(cliente: ClienteDaApi, habilitada: boolean) {
  return useQuery({
    queryKey: CHAVES.documentos,
    queryFn: ({ signal }) =>
      cliente.obter<ListaDeDocumentos>("/admin/v1/documentos", { sinal: signal }),
    enabled: habilitada,
    refetchInterval: INTERVALOS.listas,
    ...POLLING,
  });
}

export function useSaudeDoProvider(cliente: ClienteDaApi, habilitada: boolean) {
  return useQuery({
    queryKey: CHAVES.saudeProvider,
    queryFn: ({ signal }) =>
      cliente.obter<Record<string, unknown>>("/admin/v1/saude-provider", { sinal: signal }),
    enabled: habilitada,
    refetchInterval: INTERVALOS.operacional,
    ...POLLING,
  });
}

/**
 * Resolver uma divergência.
 *
 * Repetir é inofensivo e **não** gera auditoria duplicada — uma linha por
 * clique repetido encheria a tabela de ruído sobre um fato que aconteceu uma
 * vez. Então a tela não precisa se defender de clique duplo com trava própria.
 */
export function useResolverDivergencia(cliente: ClienteDaApi) {
  const cache = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      cliente.enviar<void>(`/admin/v1/divergencias/${id}/resolucao`, { metodo: "POST" }),
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: CHAVES.divergencias });
      // A contagem vive na visão geral, que está na moldura em toda tela.
      void cache.invalidateQueries({ queryKey: CHAVES.visaoGeral });
    },
  });
}

/**
 * Reprocessar mensagens da fila morta.
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ ESTA AÇÃO CONSOME. A AMOSTRA NÃO.                                        │
 * │                                                                          │
 * │ São garantias opostas de propósito: uma diagnostica, a outra conserta. A │
 * │ mensagem só sai da fila morta depois de a publicação ser confirmada, e   │
 * │ uma publicação que não encontra fila de trabalho devolve a mensagem em   │
 * │ vez de perdê-la — a fila morta é o último lugar onde o pedido do cliente │
 * │ existe.                                                                  │
 * └──────────────────────────────────────────────────────────────────────────┘
 */
export function useReprocessarFilaMorta(cliente: ClienteDaApi) {
  const cache = useQueryClient();

  return useMutation({
    mutationFn: (pedido: { fila: string; causa?: string; semCausa?: boolean; limite?: number }) =>
      cliente.enviar<{
        fila?: string;
        movidas?: number;
        ignoradas?: number;
        restam?: boolean;
        falha?: string;
      }>(`/admin/v1/filas/${encodeURIComponent(pedido.fila)}/reprocessamento`, {
        metodo: "POST",
        corpo: {
          ...(pedido.causa !== undefined ? { causa: pedido.causa } : {}),
          ...(pedido.semCausa === true ? { sem_causa: true } : {}),
          ...(pedido.limite !== undefined ? { limite: pedido.limite } : {}),
        },
      }),
    onSuccess: (_, pedido) => {
      void cache.invalidateQueries({ queryKey: CHAVES.filas });
      void cache.invalidateQueries({ queryKey: CHAVES.amostra(pedido.fila) });
      void cache.invalidateQueries({ queryKey: CHAVES.visaoGeral });
    },
  });
}

/* ── Cadastro ────────────────────────────────────────────────────────────── */

export function useSistemas(cliente: ClienteDaApi, habilitada: boolean) {
  return useQuery({
    queryKey: CHAVES.sistemas,
    queryFn: ({ signal }) =>
      cliente.obter<ListaDeSistemas>("/admin/v1/sistemas", { sinal: signal }),
    enabled: habilitada,
    // Cadastro muda por ação humana, e não sozinho: atualização manual, como o
    // doc 06 §3 define para estas telas.
    refetchInterval: false,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

export function useSistema(cliente: ClienteDaApi, id: string | null) {
  return useQuery({
    queryKey: CHAVES.sistema(id ?? ""),
    queryFn: ({ signal }) =>
      cliente.obter<DetalheDoSistema>(`/admin/v1/sistemas/${id ?? ""}`, { sinal: signal }),
    enabled: id !== null,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

export function usePessoas(cliente: ClienteDaApi, habilitada: boolean) {
  return useQuery({
    queryKey: CHAVES.pessoas,
    queryFn: ({ signal }) =>
      cliente.obter<ListaDePessoas>("/admin/v1/pessoas", { sinal: signal }),
    enabled: habilitada,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

export function useConvenios(cliente: ClienteDaApi, habilitada: boolean) {
  return useQuery({
    queryKey: CHAVES.convenios,
    queryFn: ({ signal }) =>
      cliente.obter<ListaDeConvenios>("/admin/v1/convenios", { sinal: signal }),
    enabled: habilitada,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

export function useConvenio(cliente: ClienteDaApi, id: string | null) {
  return useQuery({
    queryKey: CHAVES.convenio(id ?? ""),
    queryFn: ({ signal }) =>
      cliente.obter<DetalheDoConvenio>(`/admin/v1/convenios/${id ?? ""}`, { sinal: signal }),
    enabled: id !== null,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

/* ── Cedente como entidade ───────────────────────────────────────────────── */

/**
 * O tamanho da página das listas de cedentes e de convênios (design D13).
 *
 * `usePessoas` e `useConvenios`, acima, seguem sem filtro: `NovoVinculo` e
 * `Faturas` leem até o teto de 200 da API, e o `proximo_cursor` denuncia que há
 * mais. Tela nova de lista usa as páginas abaixo.
 */
export const TAMANHO_DA_PAGINA = 50;

export type FiltrosDePessoas = {
  q?: string | undefined;
  banco?: string | undefined;
  cursor?: string | undefined;
  limite?: number | undefined;
};

export type FiltrosDeConvenios = FiltrosDePessoas & {
  pessoa_id?: string | undefined;
  status?: string | undefined;
  certificado?: string | undefined;
};

/*
 * Sem polling, nas duas páginas: cadastro muda por ato humano (doc 06 §3), e
 * `placeholderData` segura a página anterior enquanto a próxima carrega. Sem ele,
 * cada tecla da busca esvaziaria a tabela.
 */
export function usePaginaDePessoas(
  cliente: ClienteDaApi,
  filtros: FiltrosDePessoas,
  habilitada: boolean,
) {
  return useQuery({
    queryKey: CHAVES.paginaDePessoas(filtros),
    queryFn: ({ signal }) =>
      cliente.obter<ListaDePessoas>("/admin/v1/pessoas", { busca: filtros, sinal: signal }),
    enabled: habilitada,
    placeholderData: (anterior) => anterior,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

export function usePaginaDeConvenios(
  cliente: ClienteDaApi,
  filtros: FiltrosDeConvenios,
  habilitada: boolean,
) {
  return useQuery({
    queryKey: CHAVES.paginaDeConvenios(filtros),
    queryFn: ({ signal }) =>
      cliente.obter<ListaDeConvenios>("/admin/v1/convenios", { busca: filtros, sinal: signal }),
    enabled: habilitada,
    placeholderData: (anterior) => anterior,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

/**
 * O cedente, com a prontidão que o servidor calculou (`pendencias-do-cedente`).
 *
 * A página e todas as abas leem daqui, pela mesma chave: o TanStack Query entrega
 * uma requisição só, e o selo do cabeçalho não discorda da aba.
 */
export function usePessoa(cliente: ClienteDaApi, id: string | null) {
  return useQuery({
    queryKey: CHAVES.pessoa(id ?? ""),
    queryFn: ({ signal }) =>
      cliente.obter<PessoaDetalhe>(`/admin/v1/pessoas/${id ?? ""}`, { sinal: signal }),
    enabled: id !== null,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

export function useContas(cliente: ClienteDaApi, pessoaID: string | null) {
  return useQuery({
    queryKey: CHAVES.contas(pessoaID ?? ""),
    queryFn: ({ signal }) =>
      cliente.obter<ListaDeContas>(`/admin/v1/pessoas/${pessoaID ?? ""}/contas`, {
        sinal: signal,
      }),
    enabled: pessoaID !== null,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

export function useConta(cliente: ClienteDaApi, pessoaID: string, contaID: string | null) {
  return useQuery({
    queryKey: CHAVES.conta(pessoaID, contaID ?? ""),
    queryFn: ({ signal }) =>
      cliente.obter<ContaDaPessoa>(`/admin/v1/pessoas/${pessoaID}/contas/${contaID ?? ""}`, {
        sinal: signal,
      }),
    enabled: contaID !== null,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

/**
 * O catálogo de bancos.
 *
 * Mora no binário da API, e só muda com deploy: uma hora de `staleTime` poupa a
 * requisição a cada modal aberto, e o F5 de quem atualiza o painel lê o novo.
 */
export function useBancos(cliente: ClienteDaApi, habilitada: boolean) {
  return useQuery({
    queryKey: CHAVES.bancos,
    queryFn: ({ signal }) => cliente.obter<ListaDeBancos>("/admin/v1/bancos", { sinal: signal }),
    enabled: habilitada,
    staleTime: 60 * 60 * 1000,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

/**
 * Emite uma chave de API.
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ A CREDENCIAL COMPLETA VEM UMA ÚNICA VEZ, NESTA RESPOSTA.                  │
 * │                                                                          │
 * │ Ela não é gravada em lugar nenhum: o banco guarda prefixo e hash, e a    │
 * │ auditoria registra o prefixo. Se a tela perder este valor — porque o     │
 * │ operador fechou o diálogo, ou porque um `invalidateQueries` remontou o   │
 * │ componente — ele não é recuperável, e a única saída é emitir outra e     │
 * │ revogar esta.                                                            │
 * │                                                                          │
 * │ Por isso o valor fica no estado de quem chamou, e a invalidação do cache │
 * │ acontece DEPOIS de ele ser mostrado.                                     │
 * └──────────────────────────────────────────────────────────────────────────┘
 */
export function useEmitirChave(cliente: ClienteDaApi, sistemaID: string) {
  const cache = useQueryClient();

  return useMutation({
    // A resposta traz a chave em claro, que só aparece uma vez. Sem isto, ela ficaria
    // em memória no `MutationCache` depois de a tela sair.
    gcTime: 0,
    mutationFn: (pedido: { ambiente: "live" | "test"; escopos: string[] }) =>
      cliente.enviar<{
        id: string;
        prefixo: string;
        ambiente: string;
        escopos: string[];
        chave: string;
        aviso?: string;
      }>(`/admin/v1/sistemas/${sistemaID}/chaves`, { metodo: "POST", corpo: pedido }),
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: CHAVES.sistema(sistemaID) });
      // A chave decide a pendência `sem_chave`.
      invalidarProntidao(cache);
    },
  });
}

export function useRevogarChave(cliente: ClienteDaApi, sistemaID: string) {
  const cache = useQueryClient();

  return useMutation({
    mutationFn: (chaveID: string) =>
      cliente.enviar<{ aviso?: string }>(`/admin/v1/chaves/${chaveID}`, { metodo: "DELETE" }),
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: CHAVES.sistema(sistemaID) });
      invalidarProntidao(cache);
    },
  });
}

/**
 * Dispara um evento sintético para o webhook do sistema.
 *
 * `200`, e não `202`: o efeito é síncrono — a chamada já aconteceu quando a
 * resposta sai, e o corpo é o diagnóstico dela. Um status de erro do cliente
 * também vem em `200`, no campo `status`: a requisição de teste funcionou, o
 * que falhou foi o destino.
 */
export function useTestarEntrega(cliente: ClienteDaApi, sistemaID: string) {
  return useMutation({
    mutationFn: () =>
      cliente.enviar<{
        url?: string;
        status?: number;
        latencia_ms?: number;
        cabecalhos?: Record<string, string>;
        corpo_enviado?: string;
        corpo_recebido?: string;
        truncado?: boolean;
        erro?: string;
      }>(`/admin/v1/sistemas/${sistemaID}/entrega/teste`, { metodo: "POST" }),
  });
}

/*
 * As redes permitidas da aplicação — change add-restricao-de-ip.
 *
 * As duas invalidam o DETALHE, e não uma consulta própria de `/redes`: a aba
 * Acesso lê o objeto `acesso` que já vem no detalhe, e uma segunda consulta da
 * mesma lista poderia mostrar, por um instante, uma rede removida numa e
 * presente na outra. A alteração vale na API pública já na requisição seguinte;
 * a tela não pode ficar atrás dela.
 */
export function useAdicionarRede(cliente: ClienteDaApi, sistemaID: string) {
  const cache = useQueryClient();

  return useMutation({
    mutationFn: (rede: RedeNova) =>
      cliente.enviar<RedePermitida>(`/admin/v1/sistemas/${sistemaID}/redes`, {
        metodo: "POST",
        corpo: rede,
      }),
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: CHAVES.sistema(sistemaID) });
    },
  });
}

export function useRemoverRede(cliente: ClienteDaApi, sistemaID: string) {
  const cache = useQueryClient();

  return useMutation({
    mutationFn: (redeID: string) =>
      cliente.enviar<void>(`/admin/v1/sistemas/${sistemaID}/redes/${redeID}`, {
        metodo: "DELETE",
      }),
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: CHAVES.sistema(sistemaID) });
    },
  });
}

export function useBloquearConvenio(cliente: ClienteDaApi, convenioID: string) {
  const cache = useQueryClient();

  return useMutation({
    mutationFn: (pedido: { bloqueado: boolean; motivo?: string }) =>
      cliente.enviar<void>(`/admin/v1/convenios/${convenioID}/bloqueio`, {
        metodo: "PUT",
        // `bloqueado` é obrigatório e sem valor implícito: omiti-lo responde
        // `400`, e não "desbloquear" — o zero-value de um booleano faria um
        // pedido de bloqueio virar o oposto.
        corpo: {
          bloqueado: pedido.bloqueado,
          ...(pedido.motivo !== undefined ? { motivo: pedido.motivo } : {}),
        },
      }),
    // Bloquear muda `convenio_bloqueado`, a linha da conta e o `emite` do par.
    onSuccess: () => invalidarCadastro(cache),
  });
}

/*
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ OS CADASTROS (change 20d).                                               │
 * │                                                                          │
 * │ Até aqui o painel enxergava tudo e não punha um cliente no ar: as seis   │
 * │ etapas do onboarding só nasciam por `INSERT` à mão, e uma delas — a      │
 * │ chave de API — tinha ferramenta (D-262).                                 │
 * │                                                                          │
 * │ Os três primeiros invalidam a lista que acabaram de mudar. O quarto      │
 * │ invalida o DETALHE do sistema, e não a lista: a listagem não mostra      │
 * │ webhook, e invalidá-la faria a tabela inteira recarregar por um campo    │
 * │ que ela não exibe.                                                       │
 * └──────────────────────────────────────────────────────────────────────────┘
 */

/*
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ A PRONTIDÃO É CALCULADA PELO SERVIDOR, E A TELA NÃO SABE DO QUE ELA      │
 * │ DEPENDE.                                                                 │
 * │                                                                          │
 * │ `pendencias-do-cedente` percorre conta, convênio, aplicação, contrato,   │
 * │ convênio liberado, chave e webhook. Tentar invalidar só "o que aquela    │
 * │ escrita mudou" repetiria a cadeia em TypeScript, que é o que o design D5 │
 * │ descartou. O invalidado é o cedente inteiro, por prefixo: `invalidate`   │
 * │ só refaz o que está montado, e a pendência muda sem recarregar a tela.   │
 * │                                                                          │
 * │ `invalidarCadastro` soma o que uma escrita de cadastro muda além disso:  │
 * │ as listas de convênios e o `emite` da aba Cedentes da aplicação.         │
 * └──────────────────────────────────────────────────────────────────────────┘
 */
function invalidarProntidao(cache: ReturnType<typeof useQueryClient>) {
  void cache.invalidateQueries({ queryKey: CHAVES.pessoas });
  void cache.invalidateQueries({ queryKey: ["pessoa"] });
}

function invalidarCadastro(cache: ReturnType<typeof useQueryClient>) {
  invalidarProntidao(cache);
  void cache.invalidateQueries({ queryKey: CHAVES.convenios });
  void cache.invalidateQueries({ queryKey: ["convenio"] });
  void cache.invalidateQueries({ queryKey: ["sistema"] });
}

/*
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ SEGREDO NÃO PASSA POR `variables`.                                       │
 * │                                                                          │
 * │ Senha, arquivo e credencial de WebService só valem nesta viagem, e o     │
 * │ `MutationCache` guarda `state.variables` de toda mutação. `gcTime: 0`    │
 * │ sozinho não tira o segredo de lá: ele só apaga a mutação sem observador, │
 * │ e numa recusa o modal segue aberto, com o hook montado e a senha no      │
 * │ cache até o próximo envio. Por isso o segredo chega por `carga`, que     │
 * │ esvazia ao sair, e a mutação nem sabe o que levou.                       │
 * │                                                                          │
 * │ `gcTime: 0` continua: a resposta não traz segredo, mas nada desta viagem │
 * │ tem por que sobreviver à tela. Sem retentativa: repetir é ato do         │
 * │ operador, e o certificado pode ter chegado.                              │
 * │                                                                          │
 * │ A releitura vem com sucesso ou com recusa, porque o provisionamento      │
 * │ grava o motivo da falha, e a tela o mostra.                              │
 * └──────────────────────────────────────────────────────────────────────────┘
 */
function useMutacaoComSegredo<Segredo, Resposta>(
  chamar: (segredo: Segredo) => Promise<Resposta>,
  aoTerminar: () => void,
) {
  const carga = useRef<Segredo | null>(null);

  const { mutateAsync, isPending } = useMutation({
    gcTime: 0,
    mutationFn: async () => {
      const segredo = carga.current;
      carga.current = null;
      if (segredo === null) throw new Error("Nada a enviar: preencha o formulário de novo.");
      return chamar(segredo);
    },
    onSettled: aoTerminar,
  });

  return {
    isPending,
    enviar: (segredo: Segredo) => {
      carga.current = segredo;
      return mutateAsync();
    },
  };
}

/** Onde o certificado entra: o convênio é o normal, e o cedente existe só no painel. */
export interface AlvoDoCertificado {
  nivel: "convenio" | "cedente";
  id: string;
}

/**
 * Troca o certificado: o `FormData` vai à API, que o repassa ao provider e o descarta.
 *
 * Invalida o cadastro inteiro: o vencimento muda a pendência, o `emite` do par e o filtro
 * da lista de convênios.
 */
export function useTrocarCertificado(cliente: ClienteDaApi, alvo: AlvoDoCertificado) {
  const cache = useQueryClient();
  const raiz = alvo.nivel === "convenio" ? "convenios" : "pessoas";

  return useMutacaoComSegredo(
    (formulario: FormData) =>
      cliente.enviarFormulario<LeituraDoCertificado>(
        `/admin/v1/${raiz}/${alvo.id}/certificado`,
        formulario,
      ),
    () => invalidarCadastro(cache),
  );
}

/** Lê o certificado no provider, sem enviar nada: é o que libera quem renovou por fora. */
export function useConferirCertificado(cliente: ClienteDaApi, convenioID: string) {
  const cache = useQueryClient();

  return useMutation({
    mutationFn: () =>
      cliente.enviar<ConferenciaDoCertificado>(
        `/admin/v1/convenios/${convenioID}/certificado/conferencia`,
        { metodo: "POST" },
      ),
    onSuccess: () => invalidarCadastro(cache),
  });
}

/** Leva cedente, conta e convênio ao provider. A credencial é repassada, e não fica. */
export function useProvisionar(cliente: ClienteDaApi, convenioID: string) {
  const cache = useQueryClient();

  return useMutacaoComSegredo(
    (pedido: PedidoDeProvisionamento) =>
      cliente.enviar<LeituraDoCertificado>(`/admin/v1/convenios/${convenioID}/provisionamento`, {
        metodo: "POST",
        corpo: pedido,
      }),
    () => invalidarCadastro(cache),
  );
}

/**
 * Altera o cedente, inclusive a situação.
 *
 * O corpo leva SÓ o que mudou. `status: "bloqueado"` exige `motivo`, e um corpo
 * sem chave nenhuma é `400 invalid_body`: formulário sem mudança não chama a rota.
 */
export function useAlterarPessoa(cliente: ClienteDaApi, pessoaID: string) {
  const cache = useQueryClient();

  return useMutation({
    mutationFn: (alteracao: AlteracaoDePessoa) =>
      cliente.enviar<void>(`/admin/v1/pessoas/${pessoaID}`, { metodo: "PATCH", corpo: alteracao }),
    onSuccess: () => invalidarCadastro(cache),
  });
}

export function useAlterarConta(cliente: ClienteDaApi, pessoaID: string, contaID: string) {
  const cache = useQueryClient();

  return useMutation({
    mutationFn: (alteracao: AlteracaoDeConta) =>
      cliente.enviar<void>(`/admin/v1/pessoas/${pessoaID}/contas/${contaID}`, {
        metodo: "PATCH",
        corpo: alteracao,
      }),
    onSuccess: () => invalidarCadastro(cache),
  });
}

/**
 * Cria o convênio na conta da linha.
 *
 * Conta e cedente vão na ROTA, e o banco vem da conta. `proximo_nosso_numero` só
 * existe aqui: nenhuma rota o altera depois, porque repetir nosso número é o risco
 * existencial do produto.
 */
export function useCriarConvenio(cliente: ClienteDaApi, pessoaID: string, contaID: string) {
  const cache = useQueryClient();

  return useMutation({
    mutationFn: (convenio: ConvenioNovo) =>
      cliente.enviar<CadastroCriado>(
        `/admin/v1/pessoas/${pessoaID}/contas/${contaID}/convenios`,
        { metodo: "POST", corpo: convenio },
      ),
    onSuccess: () => invalidarCadastro(cache),
  });
}

/** Sem `status`: bloquear é `useBloquearConvenio`, com a rota própria. */
export function useAlterarConvenio(cliente: ClienteDaApi, convenioID: string) {
  const cache = useQueryClient();

  return useMutation({
    mutationFn: (alteracao: AlteracaoDeConvenio) =>
      cliente.enviar<void>(`/admin/v1/convenios/${convenioID}`, {
        metodo: "PATCH",
        corpo: alteracao,
      }),
    onSuccess: () => invalidarCadastro(cache),
  });
}

export function useCriarPessoa(cliente: ClienteDaApi) {
  const cache = useQueryClient();

  return useMutation({
    mutationFn: (pessoa: PessoaNova) =>
      cliente.enviar<CadastroCriado>("/admin/v1/pessoas", { metodo: "POST", corpo: pessoa }),
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: CHAVES.pessoas });
    },
  });
}

export function useCriarConta(cliente: ClienteDaApi, pessoaID: string) {
  const cache = useQueryClient();

  return useMutation({
    // A pessoa vai na ROTA, e não no corpo: mandá-la no corpo levaria `400`,
    // porque a rota recusa campo que o contrato não descreve.
    mutationFn: (conta: ContaNova) =>
      cliente.enviar<CadastroCriado>(`/admin/v1/pessoas/${pessoaID}/contas`, {
        metodo: "POST",
        corpo: conta,
      }),
    onSuccess: () => invalidarProntidao(cache),
  });
}

export function useCriarSistema(cliente: ClienteDaApi) {
  const cache = useQueryClient();

  return useMutation({
    mutationFn: (sistema: SistemaNovo) =>
      cliente.enviar<CadastroCriado>("/admin/v1/sistemas", { metodo: "POST", corpo: sistema }),
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: CHAVES.sistemas });
    },
  });
}

export function useConfigurarEntrega(cliente: ClienteDaApi, sistemaID: string) {
  const cache = useQueryClient();

  return useMutation({
    mutationFn: (entrega: EntregaConfigurada) =>
      cliente.enviar<void>(`/admin/v1/sistemas/${sistemaID}/entrega`, {
        metodo: "PUT",
        corpo: entrega,
      }),
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: CHAVES.sistema(sistemaID) });
      // A lista mostra o estado da entrega, que toda reconfiguração zera.
      void cache.invalidateQueries({ queryKey: CHAVES.sistemas });
      // O alerta de webhook sem segredo vive na visão geral.
      void cache.invalidateQueries({ queryKey: CHAVES.visaoGeral });
      // O webhook decide o aviso `entrega_sem_webhook`.
      invalidarProntidao(cache);
    },
  });
}

/* ── Planos e contratos ──────────────────────────────────────────────────── */

const SEM_POLLING = {
  refetchInterval: false,
  refetchOnWindowFocus: false,
  retry: false,
} as const;

export function usePlanos(cliente: ClienteDaApi, habilitada: boolean) {
  return useQuery({
    queryKey: CHAVES.planos,
    queryFn: ({ signal }) => cliente.obter<ListaDePlanos>("/admin/v1/planos", { sinal: signal }),
    enabled: habilitada,
    ...SEM_POLLING,
  });
}

export function usePlano(cliente: ClienteDaApi, id: string | null) {
  return useQuery({
    queryKey: CHAVES.plano(id ?? ""),
    queryFn: ({ signal }) =>
      cliente.obter<DetalheDoPlano>(`/admin/v1/planos/${id ?? ""}`, { sinal: signal }),
    enabled: id !== null,
    ...SEM_POLLING,
  });
}

/**
 * De quem é o contrato: da aplicação (sem `pessoaID`) ou do par aplicação ×
 * cedente (change `add-cobranca-por-cedente`).
 *
 * As rotas do par são as da aplicação com `/vinculos/{pessoa_id}` no meio, e
 * as regras são as mesmas — vigência, sobreposição, troca, encerramento. Um
 * escopo, e não um segundo conjunto de ganchos, para que as duas telas não
 * divirjam no dia em que uma regra mudar.
 */
export interface EscopoDeContrato {
  sistemaID: string;
  pessoaID?: string | undefined;
}

function caminhoDosContratos(escopo: EscopoDeContrato): string {
  const base = `/admin/v1/sistemas/${escopo.sistemaID}`;
  return escopo.pessoaID === undefined
    ? `${base}/contratos`
    : `${base}/vinculos/${escopo.pessoaID}/contratos`;
}

export function useContratos(
  cliente: ClienteDaApi,
  escopo: EscopoDeContrato,
  habilitada: boolean,
) {
  return useQuery({
    queryKey: CHAVES.contratos(escopo.sistemaID, escopo.pessoaID),
    queryFn: ({ signal }) =>
      cliente.obter<ListaDeContratos>(caminhoDosContratos(escopo), { sinal: signal }),
    enabled: habilitada,
    ...SEM_POLLING,
  });
}

/** Os cedentes da aplicação, com contrato e consumo do par. */
export function useCedentes(cliente: ClienteDaApi, sistemaID: string, habilitada: boolean) {
  return useQuery({
    queryKey: CHAVES.cedentes(sistemaID),
    queryFn: ({ signal }) =>
      cliente.obter<ListaDeCedentes>(`/admin/v1/sistemas/${sistemaID}/vinculos`, {
        sinal: signal,
      }),
    enabled: habilitada,
    ...SEM_POLLING,
  });
}

/** As aplicações a que o beneficiário está vinculado. */
export function useVinculosDoBeneficiario(cliente: ClienteDaApi, pessoaID: string | null) {
  return useQuery({
    queryKey: CHAVES.vinculosDaPessoa(pessoaID ?? ""),
    queryFn: ({ signal }) =>
      cliente.obter<ListaDeVinculosDoBeneficiario>(
        `/admin/v1/pessoas/${pessoaID ?? ""}/vinculos`,
        { sinal: signal },
      ),
    enabled: pessoaID !== null,
    ...SEM_POLLING,
  });
}

/*
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ PLANO E CONTRATO SE ENXERGAM DOS DOIS LADOS.                             │
 * │                                                                          │
 * │ A lista de planos conta contratos vigentes, e o detalhe da aplicação     │
 * │ mostra franquia e política do plano. Mexer num lado sem invalidar o      │
 * │ outro deixa uma tela dizendo "2 contratos" enquanto a outra mostra que   │
 * │ o contrato acabou de ser encerrado.                                      │
 * │                                                                          │
 * │ Por isso as duas famílias de ação invalidam por PREFIXO (`["plano"]`,    │
 * │ `["sistema"]`): é mais requisição do que o mínimo, numa tela de          │
 * │ cadastro que muda por clique humano, e nenhuma combinação fica velha.    │
 * └──────────────────────────────────────────────────────────────────────────┘
 */
function invalidarPlanosEContratos(cache: ReturnType<typeof useQueryClient>) {
  void cache.invalidateQueries({ queryKey: CHAVES.planos });
  void cache.invalidateQueries({ queryKey: ["plano"] });
  void cache.invalidateQueries({ queryKey: CHAVES.sistemas });
  void cache.invalidateQueries({ queryKey: ["sistema"] });
  // O contrato decide as pendências de contrato, na página e na linha da lista.
  invalidarProntidao(cache);
}

export function useCriarPlano(cliente: ClienteDaApi) {
  const cache = useQueryClient();

  return useMutation({
    mutationFn: (plano: PlanoNovo) =>
      cliente.enviar<CadastroCriado>("/admin/v1/planos", { metodo: "POST", corpo: plano }),
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: CHAVES.planos });
    },
  });
}

/**
 * Altera o plano.
 *
 * O corpo leva SÓ o que mudou: campo ausente é "não mexa", e `null` é "limpe"
 * (sem limite, ilimitado). Mandar o plano inteiro de volta faria uma edição de
 * nome exigir a confirmação de contratos vigentes, porque o servidor veria
 * campos comerciais no pedido.
 */
export function useAlterarPlano(cliente: ClienteDaApi, planoID: string) {
  const cache = useQueryClient();

  return useMutation({
    mutationFn: (alteracao: AlteracaoDePlano) =>
      cliente.enviar<PlanoAlterado>(`/admin/v1/planos/${planoID}`, {
        metodo: "PATCH",
        corpo: alteracao,
      }),
    onSuccess: () => invalidarPlanosEContratos(cache),
  });
}

export function useCriarContrato(cliente: ClienteDaApi, escopo: EscopoDeContrato) {
  const cache = useQueryClient();

  return useMutation({
    mutationFn: (contrato: ContratoNovo) =>
      cliente.enviar<CadastroCriado>(caminhoDosContratos(escopo), {
        metodo: "POST",
        corpo: contrato,
      }),
    onSuccess: () => invalidarPlanosEContratos(cache),
  });
}

export function useEncerrarContrato(cliente: ClienteDaApi, escopo: EscopoDeContrato) {
  const cache = useQueryClient();

  return useMutation({
    mutationFn: (pedido: { contratoID: string; corpo: EncerramentoDeContrato }) =>
      cliente.enviar<Contrato>(
        `${caminhoDosContratos(escopo)}/${pedido.contratoID}/encerramento`,
        { metodo: "POST", corpo: pedido.corpo },
      ),
    onSuccess: () => invalidarPlanosEContratos(cache),
  });
}

/**
 * Troca de plano: o servidor encerra o vigente na véspera e abre o novo na
 * mesma transação. A tela não faz isso em dois pedidos — na falha do segundo,
 * a aplicação ficaria sem contrato e pararia de emitir.
 */
export function useTrocarPlano(cliente: ClienteDaApi, escopo: EscopoDeContrato) {
  const cache = useQueryClient();

  return useMutation({
    mutationFn: (troca: TrocaDePlano) =>
      cliente.enviar<TrocaRealizada>(`${caminhoDosContratos(escopo)}/troca`, {
        metodo: "POST",
        corpo: troca,
      }),
    onSuccess: () => invalidarPlanosEContratos(cache),
  });
}

/* ── Cobrança por cedente ────────────────────────────────────────────────── */

/**
 * Modalidade e plano padrão da aplicação.
 *
 * O corpo leva só o que muda: a troca de modalidade é recusada com consumo na
 * competência (`409 modalidade_com_consumo`), e mandar a modalidade de volta,
 * igual, numa edição do plano padrão faria a mesma pergunta ao servidor sem
 * necessidade.
 */
export function useAlterarCobranca(cliente: ClienteDaApi, sistemaID: string) {
  const cache = useQueryClient();

  return useMutation({
    mutationFn: (alteracao: AlteracaoDeCobranca) =>
      cliente.enviar<CobrancaDoSistema>(`/admin/v1/sistemas/${sistemaID}`, {
        metodo: "PATCH",
        corpo: alteracao,
      }),
    onSuccess: () => invalidarPlanosEContratos(cache),
  });
}

export function useCriarVinculo(cliente: ClienteDaApi, sistemaID: string) {
  const cache = useQueryClient();

  return useMutation({
    mutationFn: (vinculo: VinculoNovo) =>
      cliente.enviar<Vinculo>(`/admin/v1/sistemas/${sistemaID}/vinculos`, {
        metodo: "POST",
        corpo: vinculo,
      }),
    // Com plano padrão, o contrato do par nasce na mesma transação: a lista de
    // planos (contagem de vigentes) muda junto.
    onSuccess: () => invalidarPlanosEContratos(cache),
  });
}

/**
 * As três escritas do vínculo que já existe: convênios permitidos, situação e
 * teto próprio. Cada uma tem rota própria, e nenhuma manda o vínculo inteiro —
 * o `PATCH` dos convênios recusa corpo sem a lista, e um corpo montado com o
 * vínculo inteiro levaria a lista antiga de volta numa troca de teto.
 */
type EscritaDoVinculo =
  | { tipo: "convenios"; corpo: ConveniosDoVinculo }
  | { tipo: "situacao"; corpo: SituacaoDoVinculo }
  | { tipo: "limite"; corpo: LimiteDoVinculo };

export function useAlterarVinculo(cliente: ClienteDaApi, sistemaID: string, pessoaID: string) {
  const cache = useQueryClient();

  return useMutation({
    mutationFn: (escrita: EscritaDoVinculo) => {
      const base = `/admin/v1/sistemas/${sistemaID}/vinculos/${pessoaID}`;
      return escrita.tipo === "convenios"
        ? cliente.enviar<void>(base, { metodo: "PATCH", corpo: escrita.corpo })
        : cliente.enviar<unknown>(`${base}/${escrita.tipo}`, {
            metodo: "PUT",
            corpo: escrita.corpo,
          });
    },
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: CHAVES.sistema(sistemaID) });
      // Situação e convênios do vínculo decidem pendências do cedente, e os
      // vínculos dele moram sob a mesma chave.
      invalidarProntidao(cache);
    },
  });
}

/* ── Faturamento ─────────────────────────────────────────────────────────── */

export type FiltrosDeFaturas = {
  competencia?: string | undefined;
  situacao?: string | undefined;
  sistema_id?: string | undefined;
  pessoa_id?: string | undefined;
  vencidas?: string | undefined;
};

/*
 * Sem polling, nas três leituras. A lista muda por ato humano (fechamento,
 * baixa, cancelamento), e cada ato invalida o prefixo. A prévia muda sozinha,
 * mas é cálculo sobre `consumo_evento` inteiro do mês: a cada 30 s, com três
 * abas abertas, seria varrer o consumo o dia todo para um número que o
 * operador consulta, e não vigia.
 */
export function useFaturas(cliente: ClienteDaApi, filtros: FiltrosDeFaturas, habilitada: boolean) {
  return useQuery({
    queryKey: CHAVES.faturas(filtros),
    queryFn: ({ signal }) =>
      cliente.obter<ListaDeFaturas>("/admin/v1/faturas", { busca: filtros, sinal: signal }),
    enabled: habilitada,
    placeholderData: (anterior) => anterior,
    ...SEM_POLLING,
  });
}

export function usePrevia(
  cliente: ClienteDaApi,
  filtros: Pick<FiltrosDeFaturas, "sistema_id" | "pessoa_id">,
  habilitada: boolean,
) {
  return useQuery({
    queryKey: CHAVES.previa(filtros),
    queryFn: ({ signal }) =>
      cliente.obter<PreviaDaCompetencia>("/admin/v1/faturas/previa", {
        busca: filtros,
        sinal: signal,
      }),
    enabled: habilitada,
    placeholderData: (anterior) => anterior,
    ...SEM_POLLING,
  });
}

export function useFatura(cliente: ClienteDaApi, id: string | null) {
  return useQuery({
    queryKey: CHAVES.fatura(id ?? ""),
    queryFn: ({ signal }) =>
      cliente.obter<Fatura>(`/admin/v1/faturas/${id ?? ""}`, { sinal: signal }),
    enabled: id !== null,
    ...SEM_POLLING,
  });
}

function invalidarFaturamento(cache: ReturnType<typeof useQueryClient>) {
  void cache.invalidateQueries({ queryKey: ["faturas"] });
  // A pendência de fechamento e as vencidas vivem na visão geral.
  void cache.invalidateQueries({ queryKey: CHAVES.visaoGeral });
}

/**
 * A simulação do fechamento: o número que a confirmação mostra.
 *
 * É mutação, e não consulta, mesmo sem gravar nada: ela roda quando o operador
 * pede, com o corpo dele, e o resultado vale para AQUELE pedido — guardá-la no
 * cache faria uma segunda abertura mostrar a simulação velha.
 */
export function useSimularFechamento(cliente: ClienteDaApi) {
  return useMutation({
    mutationFn: (pedido: PedidoDeFechamento) =>
      cliente.enviar<SimulacaoDeFechamento>("/admin/v1/faturas/fechamentos/simulacao", {
        metodo: "POST",
        corpo: pedido,
      }),
  });
}

/**
 * O fechamento, com a `marca` da simulação que o operador confirmou.
 *
 * Marca diferente da atual volta `409 simulacao_desatualizada` com a simulação
 * nova — quem chama mostra a nova e pergunta de novo, como na confirmação de
 * plano. Gravar sobre números que ninguém viu é o que a marca existe para
 * impedir.
 */
export function useFecharCompetencia(cliente: ClienteDaApi) {
  const cache = useQueryClient();

  return useMutation({
    mutationFn: (pedido: PedidoDeFechamento) =>
      cliente.enviar<ResultadoDoFechamento>("/admin/v1/faturas/fechamentos", {
        metodo: "POST",
        corpo: pedido,
      }),
    onSuccess: () => invalidarFaturamento(cache),
  });
}

export function useBaixarFatura(cliente: ClienteDaApi, faturaID: string) {
  const cache = useQueryClient();

  return useMutation({
    mutationFn: (baixa: BaixaDeFatura) =>
      cliente.enviar<Fatura>(`/admin/v1/faturas/${faturaID}/baixa`, {
        metodo: "POST",
        corpo: baixa,
      }),
    // No erro também, pelo mesmo motivo do cancelamento, logo abaixo.
    onSettled: () => invalidarFaturamento(cache),
  });
}

export function useCancelarFatura(cliente: ClienteDaApi, faturaID: string) {
  const cache = useQueryClient();

  return useMutation({
    mutationFn: (cancelamento: CancelamentoDeFatura) =>
      cliente.enviar<Fatura>(`/admin/v1/faturas/${faturaID}/cancelamento`, {
        metodo: "POST",
        corpo: cancelamento,
      }),
    /*
     * Também no ERRO: `409 fatura_fora_da_situacao` quer dizer que outra pessoa
     * deu baixa nesse meio tempo, e o detalhe na tela está velho. Recarregar
     * mostra a situação de verdade atrás da recusa.
     */
    onSettled: () => invalidarFaturamento(cache),
  });
}

/* ── Listas que crescem por "Carregar mais" ──────────────────────────────── */

/**
 * O teto da página de boletos, entregas e auditoria (`ports.MaxPaginaDoPainel`), e o que
 * estas rotas devolvem quando ninguém pede `limite`. Elas não trazem total nem próximo
 * cursor: página cheia é o único sinal de que há mais.
 */
export const TETO_DA_PAGINA = 200;

export interface ListaQueCresce<T> {
  /** Da linha mais nova para a mais antiga, sem repetir nenhuma. */
  itens: readonly T[];
  /** A primeira página ainda não chegou. */
  carregando: boolean;
  /** A falha da primeira página: a lista inteira depende dela. */
  erro: Error | null;
  /** A última página trazida veio cheia. É o que mostra o botão. */
  temMais: boolean;
  carregandoMais: boolean;
  /** A falha da página seguinte: o que já está na tela continua, e o botão repete. */
  erroAoCarregarMais: Error | null;
  carregarMais: () => void;
}

/*
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ SÓ A PRIMEIRA PÁGINA FAZ POLLING, E NENHUMA LINHA SE REPETE NEM SOME.    │
 * │                                                                          │
 * │ `useInfiniteQuery` sozinho refaria todas as páginas a cada 30 s (design  │
 * │ D5). Aqui a primeira é a consulta de sempre, e a sequência é outra, sem  │
 * │ intervalo, debaixo da MESMA chave: quem invalida o prefixo leva as duas, │
 * │ e a linha resolvida na página três sai da tela sem esperar nada.         │
 * │                                                                          │
 * │ As três rotas ordenam por `id DESC`, e UUIDv7 em minúsculas compara como │
 * │ texto na ordem do banco. Por isso a junção é uma conta só: acima da      │
 * │ última linha viva quem manda é o polling, e abaixo dela, o que foi lido. │
 * │ O boleto novo empurra a lista, e a linha empurrada para fora da primeira │
 * │ página aparece uma vez, de `retidas`. Sem elas ela sumiria: a sequência  │
 * │ começa depois do cursor, e a primeira página já não a traz. Dublê com    │
 * │ `id` fora de ordem cai nesta conta: use `umaPaginaDeBoletos`.            │
 * │                                                                          │
 * │ Linha só em `retidas` não atualiza: é o preço de não reler a lista, e o  │
 * │ detalhe aberto lê a verdade por `id`.                                    │
 * └──────────────────────────────────────────────────────────────────────────┘
 */
function useListaQueCresce<T extends { id: string }>({
  chave,
  consulta,
  primeira,
  buscar,
}: {
  /** A chave da primeira página. A sequência mora debaixo dela. */
  chave: readonly unknown[];
  consulta: { isPending: boolean; isPlaceholderData: boolean; error: Error | null };
  primeira: readonly T[] | undefined;
  buscar: (antes: string, sinal: AbortSignal) => Promise<readonly T[]>;
}): ListaQueCresce<T> {
  const identidade = JSON.stringify(chave);
  const [inicio, setInicio] = useState<{
    identidade: string;
    cursor: string;
    retidas: readonly T[];
  } | null>(null);

  // Filtro novo descarta o acumulado aqui mesmo, antes de a tela pintar a lista antiga.
  if (inicio !== null && inicio.identidade !== identidade) setInicio(null);
  const vigente = inicio?.identidade === identidade ? inicio : null;

  const seguintes = useInfiniteQuery({
    queryKey: [...chave, "seguintes", vigente?.cursor ?? ""],
    queryFn: ({ pageParam, signal }) => buscar(pageParam, signal),
    initialPageParam: vigente?.cursor ?? "",
    getNextPageParam: (ultima) =>
      ultima.length === TETO_DA_PAGINA ? ultima.at(-1)?.id : undefined,
    enabled: vigente !== null,
    ...SEM_POLLING,
  });

  const vivas = primeira ?? [];
  const ultimaViva = vivas.at(-1)?.id;
  const abaixo = [
    ...(vigente?.retidas ?? []),
    ...(seguintes.data?.pages ?? []).flatMap((pagina) => pagina),
  ].filter((item) => ultimaViva === undefined || item.id < ultimaViva);

  const cheia =
    vigente === null
      ? vivas.length === TETO_DA_PAGINA
      : seguintes.data === undefined || seguintes.hasNextPage;

  return {
    itens: [...vivas, ...abaixo],
    carregando: consulta.isPending,
    erro: consulta.error,
    // A página provisória é a do filtro anterior: o cursor dela não vale para o novo.
    temMais: cheia && !consulta.isPlaceholderData,
    carregandoMais: vigente !== null && (seguintes.isPending || seguintes.isFetchingNextPage),
    erroAoCarregarMais: seguintes.error,
    carregarMais: () => {
      if (vigente !== null) {
        void (seguintes.data === undefined ? seguintes.refetch() : seguintes.fetchNextPage());
        return;
      }
      if (ultimaViva !== undefined) {
        setInicio({ identidade, cursor: ultimaViva, retidas: vivas });
      }
    },
  };
}

/** A lista de boletos com "Carregar mais". A primeira página é a de `useBoletos`. */
export function useListaDeBoletos(
  cliente: ClienteDaApi,
  filtros: FiltrosDeBoletos,
  habilitada: boolean,
): ListaQueCresce<BoletoDoPainel> {
  const consulta = useBoletos(cliente, filtros, habilitada);

  return useListaQueCresce({
    chave: CHAVES.boletos(filtros),
    consulta,
    primeira: consulta.data?.boletos,
    buscar: async (antes, sinal) => {
      const pagina = await cliente.obter<ListaDeBoletos>("/admin/v1/boletos", {
        busca: { ...filtros, antes },
        sinal,
      });
      return pagina.boletos;
    },
  });
}

/* ── Exportação, verificação e ficha ─────────────────────────────────────── */

/**
 * O CSV de boletos, com os filtros da tela.
 *
 * Vem do servidor, e não da página que está na tela: a página tem teto de 200, e quem
 * mascara por papel, conta o corte e audita é a rota. `truncado` é o aviso que a tela deve
 * dar: o arquivo parou nos 10.000 mais recentes.
 */
export function useExportarBoletos(cliente: ClienteDaApi) {
  return useMutation({
    mutationFn: async (filtros: Omit<FiltrosDeBoletos, "antes">) => {
      const arquivo = await cliente.baixar("/admin/v1/boletos.csv", "boletos.csv", {
        busca: filtros,
        aceita: "text/csv",
      });
      salvar(arquivo.conteudo, arquivo.nome);
      return { truncado: arquivo.cabecalhos.get("x-exportacao-truncada") === "true" };
    },
  });
}

/**
 * "Verificar agora": antecipa a consulta da conciliação.
 *
 * `202`, e o resultado chega depois: quem consulta é o conciliador, e o detalhe mostra em
 * "Última consulta". `exact` porque a aba técnica mora sob esta chave, e reler a aba grava
 * uma linha de auditoria. No erro também: `verificacao_recente` diz que outro operador
 * acabou de pedir, e o instante dele está no detalhe.
 */
export function useVerificarBoleto(cliente: ClienteDaApi, id: string) {
  const cache = useQueryClient();

  return useMutation({
    mutationFn: () =>
      cliente.enviar<{ verificacao_pedida_em: string }>(`/admin/v1/boletos/${id}/verificacao`, {
        metodo: "POST",
      }),
    onSettled: () => cache.invalidateQueries({ queryKey: CHAVES.boleto(id), exact: true }),
  });
}

/** Como a ficha chegou: baixada, ou o link do provider para a tela abrir com `noopener`. */
export type FichaDoBoleto = { via: "arquivo" } | { via: "link"; link: string };

/**
 * "Ver PDF". Nunca reemissão: é o mesmo título, com a mesma linha digitável.
 *
 * O `200` tem dois corpos, e quem decide é o `Content-Type`: `application/pdf` é baixado, e
 * `application/json` é a degradação, com `{link}`. O contrato promete `https`, e a guarda
 * fica aqui porque a tela vai abrir o que sair deste gancho.
 */
export function useVerPDF(cliente: ClienteDaApi, id: string) {
  return useMutation({
    mutationFn: async (): Promise<FichaDoBoleto> => {
      const arquivo = await cliente.baixar(`/admin/v1/boletos/${id}/pdf`, `boleto-${id}.pdf`, {
        aceita: "application/pdf, application/json",
      });

      if (!(arquivo.cabecalhos.get("content-type") ?? "").includes("application/json")) {
        salvar(arquivo.conteudo, arquivo.nome);
        return { via: "arquivo" };
      }

      const { link } = JSON.parse(await arquivo.conteudo.text()) as LinkDaFicha;
      if (!link.startsWith("https://")) throw new Error("A ficha não está disponível agora.");
      return { via: "link", link };
    },
  });
}

/* ── Entregas ────────────────────────────────────────────────────────────── */

export type FiltrosDeEntregas = {
  sistema_id?: string | undefined;
  estado?: string | undefined;
  tipo?: string | undefined;
  boleto_id?: string | undefined;
  criado_de?: string | undefined;
  criado_ate?: string | undefined;
};

/** A lista de entregas, com "Carregar mais" e a primeira página em polling de lista. */
export function useListaDeEntregas(
  cliente: ClienteDaApi,
  filtros: FiltrosDeEntregas,
  habilitada: boolean,
): ListaQueCresce<EntregaDoPainel> {
  const consulta = useQuery({
    queryKey: CHAVES.entregas(filtros),
    queryFn: ({ signal }) =>
      cliente.obter<ListaDeEntregas>("/admin/v1/entregas", { busca: filtros, sinal: signal }),
    enabled: habilitada,
    refetchInterval: INTERVALOS.listas,
    placeholderData: (anterior) => anterior,
    ...POLLING,
  });

  return useListaQueCresce({
    chave: CHAVES.entregas(filtros),
    consulta,
    primeira: consulta.data?.entregas,
    buscar: async (antes, sinal) => {
      const pagina = await cliente.obter<ListaDeEntregas>("/admin/v1/entregas", {
        busca: { ...filtros, antes },
        sinal,
      });
      return pagina.entregas;
    },
  });
}

/**
 * Reenvia a MESMA linha: mesmo `evento_id`, payload congelado, uma tentativa por clique.
 *
 * `justificativa` só em `boleto.falhou`, com dez caracteres ou mais. No erro a lista
 * também recarrega: `409` quer dizer que a linha mudou, e a tela está velha.
 */
export function useReenviarEntrega(cliente: ClienteDaApi) {
  const cache = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...corpo }: { id: string } & ReenvioDeEntrega) =>
      cliente.enviar<ReenvioAgendado>(`/admin/v1/entregas/${id}/reenvio`, {
        metodo: "POST",
        ...(corpo.justificativa !== undefined ? { corpo } : {}),
      }),
    onSettled: () => cache.invalidateQueries({ queryKey: ["entregas"] }),
  });
}

/* ── Auditoria ───────────────────────────────────────────────────────────── */

export type FiltrosDaAuditoria = {
  operador_id?: string | undefined;
  /** Fora do catálogo a rota responde `400`, e não uma lista vazia. */
  acao?: string | undefined;
  entidade?: string | undefined;
  entidade_id?: string | undefined;
  ocorrido_de?: string | undefined;
  ocorrido_ate?: string | undefined;
};

/** A trilha, com "Carregar mais". Exige `operador`: `habilitada` confere o papel. */
export function useListaDaAuditoria(
  cliente: ClienteDaApi,
  filtros: FiltrosDaAuditoria,
  habilitada: boolean,
): ListaQueCresce<LinhaDaAuditoria> {
  const consulta = useQuery({
    queryKey: CHAVES.auditoria(filtros),
    queryFn: ({ signal }) =>
      cliente.obter<ListaDaAuditoria>("/admin/v1/auditoria", { busca: filtros, sinal: signal }),
    enabled: habilitada,
    refetchInterval: INTERVALOS.listas,
    placeholderData: (anterior) => anterior,
    ...POLLING,
  });

  return useListaQueCresce({
    chave: CHAVES.auditoria(filtros),
    consulta,
    primeira: consulta.data?.auditoria,
    buscar: async (antes, sinal) => {
      const pagina = await cliente.obter<ListaDaAuditoria>("/admin/v1/auditoria", {
        busca: { ...filtros, antes },
        sinal,
      });
      return pagina.auditoria;
    },
  });
}

/* ── Ciclo de vida da aplicação ──────────────────────────────────────────── */

/*
 * Situação e nome aparecem no cabeçalho, na lista e nos vínculos, e `aplicacao_suspensa` é
 * pendência de cedente. Tudo no `onSettled`: `409 sistema_fora_da_situacao` quer dizer que
 * outro operador já mudou, e a tela precisa mostrar a situação de verdade atrás da recusa.
 */
function invalidarAplicacao(cache: ReturnType<typeof useQueryClient>, sistemaID: string) {
  void cache.invalidateQueries({ queryKey: CHAVES.sistemas });
  void cache.invalidateQueries({ queryKey: CHAVES.sistema(sistemaID) });
  invalidarProntidao(cache);
}

/** Suspende ou reativa. Vale na chamada seguinte da API: a autenticação lê sem cache. */
export function useDefinirSituacaoDoSistema(cliente: ClienteDaApi, sistemaID: string) {
  const cache = useQueryClient();

  return useMutation({
    mutationFn: (pedido: PedidoDeSituacaoDoSistema) =>
      cliente.enviar<{ status: SituacaoPedidaDoSistema }>(
        `/admin/v1/sistemas/${sistemaID}/situacao`,
        { metodo: "PUT", corpo: pedido },
      ),
    onSettled: () => invalidarAplicacao(cache, sistemaID),
  });
}

export function useCorrigirNomeDoSistema(cliente: ClienteDaApi, sistemaID: string) {
  const cache = useQueryClient();

  return useMutation({
    mutationFn: (correcao: CorrecaoDeNomeDoSistema) =>
      cliente.enviar<{ nome: string }>(`/admin/v1/sistemas/${sistemaID}/nome`, {
        metodo: "PUT",
        corpo: correcao,
      }),
    onSettled: () => invalidarAplicacao(cache, sistemaID),
  });
}
